import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { InstructorStatus } from '../types/admin';

export interface PermissionCheck {
  allowed: boolean;
  loading: boolean;
  reason?: string;
  redirectTo?: string;
}

export interface PermissionGuardOptions {
  requireRole?: 'student' | 'instructor' | 'admin';
  requireInstructorStatus?: InstructorStatus[];
  checkTrialExpiry?: boolean;
  requirePlan?: string[];
  autoRedirect?: boolean;
}

export function usePermissionGuard(options: PermissionGuardOptions = {}): PermissionCheck {
  const { user, loading: authLoading } = useAuthContext();
  const navigate = useNavigate();
  const [check, setCheck] = useState<PermissionCheck>({
    allowed: false,
    loading: true,
  });

  useEffect(() => {
    async function checkPermissions() {
      if (authLoading) {
        setCheck({ allowed: false, loading: true });
        return;
      }

      if (!user) {
        setCheck({
          allowed: false,
          loading: false,
          reason: 'กรุณาเข้าสู่ระบบ',
          redirectTo: '/login',
        });
        
        if (options.autoRedirect) {
          navigate('/login');
        }
        return;
      }

      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
          setCheck({
            allowed: false,
            loading: false,
            reason: 'ไม่พบข้อมูลผู้ใช้',
            redirectTo: '/profile',
          });
          return;
        }

        const userData = userDoc.data();

        if (options.requireRole && userData.role !== options.requireRole) {
          setCheck({
            allowed: false,
            loading: false,
            reason: `คุณไม่มีสิทธิ์เข้าถึงหน้านี้ (ต้องการบทบาท: ${options.requireRole})`,
            redirectTo: '/',
          });
          
          if (options.autoRedirect) {
            navigate('/not-allowed');
          }
          return;
        }

        if (options.requireInstructorStatus && userData.role === 'instructor') {
          const instructorStatus = userData.instructorStatus || 'active';
          
          if (!options.requireInstructorStatus.includes(instructorStatus)) {
            let reason = '';
            let redirectTo = '/instructor';
            
            if (instructorStatus === 'suspended') {
              reason = 'บัญชีของคุณถูกระงับ กรุณาติดต่อผู้ดูแลระบบ';
              redirectTo = '/suspended';
            } else if (instructorStatus === 'limited') {
              reason = 'บัญชีของคุณถูกจำกัดสิทธิ์ ไม่สามารถสร้างคอร์สใหม่ได้';
              redirectTo = '/instructor';
            }
            
            setCheck({
              allowed: false,
              loading: false,
              reason,
              redirectTo,
            });
            
            if (options.autoRedirect) {
              navigate(redirectTo);
            }
            return;
          }
        }

        if (options.checkTrialExpiry && userData.role === 'instructor') {
          const trialExpired = userData.trialExpired || false;
          const plan = userData.plan || 'free';
          
          if (plan === 'free' && trialExpired) {
            setCheck({
              allowed: false,
              loading: false,
              reason: 'Trial ของคุณหมดอายุแล้ว กรุณาอัปเกรดแพ็กเกจเพื่อใช้งานต่อ',
              redirectTo: '/pricing',
            });
            
            if (options.autoRedirect) {
              navigate('/pricing');
            }
            return;
          }
        }

        if (options.requirePlan && options.requirePlan.length > 0) {
          const plan = userData.plan || 'free';
          
          if (!options.requirePlan.includes(plan)) {
            setCheck({
              allowed: false,
              loading: false,
              reason: `ฟีเจอร์นี้ใช้ได้เฉพาะแพ็กเกจ: ${options.requirePlan.join(', ')}`,
              redirectTo: '/pricing',
            });
            
            if (options.autoRedirect) {
              navigate('/pricing');
            }
            return;
          }
        }

        setCheck({
          allowed: true,
          loading: false,
        });
        
      } catch (error) {
        console.error('[PermissionGuard] Error checking permissions:', error);
        setCheck({
          allowed: false,
          loading: false,
          reason: 'เกิดข้อผิดพลาดในการตรวจสอบสิทธิ์',
          redirectTo: '/',
        });
      }
    }

    checkPermissions();
  }, [user, authLoading, options.requireRole, options.checkTrialExpiry, options.autoRedirect, navigate]);

  return check;
}

export function useInstructorGuard(autoRedirect: boolean = true): PermissionCheck {
  return usePermissionGuard({
    requireRole: 'instructor',
    requireInstructorStatus: ['active', 'limited'],
    checkTrialExpiry: true,
    autoRedirect,
  });
}

export function useAdminGuard(autoRedirect: boolean = true): PermissionCheck {
  return usePermissionGuard({
    requireRole: 'admin',
    autoRedirect,
  });
}

export function useCreateCourseGuard(autoRedirect: boolean = true): PermissionCheck {
  return usePermissionGuard({
    requireRole: 'instructor',
    requireInstructorStatus: ['active'],
    checkTrialExpiry: true,
    autoRedirect,
  });
}
